import React from "react";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { CourseContentBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/CourseContentBlockEditor";
import { TagsInput } from "@/modules/learningCenter/features/ModuleLibrary/components/TagsInput/TagsInput";
import { LibraryModuleFormData } from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleForm/types";
import {
  LibraryModule,
  LIBRARY_MODULE_DIFFICULTY_OPTIONS,
} from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

const NONE_VALUE = "__none__";

interface LibraryModuleFormProps {
  mode: "create" | "edit";
  form: UseFormReturn<LibraryModuleFormData>;
  isSubmitting: boolean;
  loading?: boolean;
  error?: string | null;
  onSubmit: () => void;
  onCancel: () => void;
  /** Edit-mode only: the loaded module (for status + publish/archive). */
  module?: LibraryModule | null;
  onPublish?: () => void;
  onArchive?: () => void;
}

export function LibraryModuleForm({
  mode,
  form,
  isSubmitting,
  loading,
  error,
  onSubmit,
  onCancel,
  module,
  onPublish,
  onArchive,
}: LibraryModuleFormProps) {
  const { control, handleSubmit } = form;
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);

  const isArchived = module?.status === "archived";

  return (
    <MainPageWrapper
      actions={
        mode === "edit" && module ? (
          <div className="flex gap-2">
            {module.status === "draft" && onPublish && (
              <Button onClick={onPublish} type="button" variant="default">
                Publish
              </Button>
            )}
            {module.status !== "archived" && onArchive && (
              <Button
                onClick={() => setShowArchiveDialog(true)}
                type="button"
                variant="outline"
              >
                Archive
              </Button>
            )}
          </div>
        ) : undefined
      }
      error={error}
      loading={loading}
      subtitle={
        mode === "create"
          ? "Create a new library module."
          : "Edit a reusable library module."
      }
      title={
        mode === "create" ? "Create Library Module" : "Edit Library Module"
      }
    >
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {isArchived && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This module is archived and can no longer be edited.
            </p>
          )}

          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input
                    disabled={isArchived}
                    placeholder="Module title"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    disabled={isArchived}
                    placeholder="Module description"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="difficulty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Difficulty</FormLabel>
                  <FormControl>
                    <Select
                      disabled={isArchived}
                      onValueChange={(val) =>
                        field.onChange(val === NONE_VALUE ? null : val)
                      }
                      value={field.value || NONE_VALUE}
                    >
                      <SelectTrigger aria-label="Difficulty">
                        <SelectValue placeholder="Select difficulty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {LIBRARY_MODULE_DIFFICULTY_OPTIONS.map((difficulty) => (
                          <SelectItem key={difficulty} value={difficulty}>
                            {difficulty.charAt(0).toUpperCase() +
                              difficulty.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="estimatedMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estimated minutes</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isArchived}
                      placeholder="e.g. 30"
                      type="number"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>
                <FormControl>
                  <TagsInput onChange={field.onChange} value={field.value} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="blocks"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content</FormLabel>
                <FormControl>
                  <CourseContentBlockEditor
                    blocks={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2">
            <Button onClick={onCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={isSubmitting || isArchived} type="submit">
              {mode === "create" ? "Create Module" : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>

      {mode === "edit" && module && onArchive && (
        <ConfirmationDialog
          cancelMessage="Cancel"
          confirmMessage="Archive"
          dialogInstructionFinalQuestion="Are you sure you want to archive this module?"
          dialogInstructionItems={[
            "The module will no longer be editable.",
            "It will be hidden from the published-module picker.",
          ]}
          dialogInstructionTitle="Archiving a library module:"
          dialogTitleContent={`Archive "${module.title}"?`}
          isOpen={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={() => {
            setShowArchiveDialog(false);
            onArchive();
          }}
        />
      )}
    </MainPageWrapper>
  );
}
